import { useState } from 'react';
import classNames from 'classnames';
import { message } from 'antd';
import type { ApolloError } from '@apollo/client';
import Cookies from 'js-cookie';
import { Segment } from '@fixter/i18n';
import { parseApolloErrors } from '$apollo/util';
import type { MutationCreateCustomerParams } from '$apollo/gql/customer';
import { useCreateCustomer } from '$apollo/gql/customer/hooks';
import { changeUrl } from '$util/index';
import { useAppContext } from '$util/AppContext';
import type { CmsContentDictionary } from '$cms/index';
import { getShortTextByKey } from '$cms/getDataByKey';
import { SimpleLink } from '@/components/atoms/SimpleLink';
import { Title } from '@/components/atoms/Title';
import { RegisterForm } from './components/RegisterForm';
import { Styled } from './styled';

const getCmsData = (cmsData: CmsContentDictionary) => {
  const getShortText = getShortTextByKey.bind(cmsData);

  return {
    formTitle: getShortText('register.formTitle', 'Create a Fixter account'),
    alreadyRegisteredText: getShortText('register.alreadyRegistered.text', 'Already have an account?'),
    loginLabel: getShortText('register.login.label', 'Login'),
    errorMessages: {
      invalidLoginDetails: getShortText(
        'register.invalidCredentialsError.text',
        'You need to use a different email and password combination'
      ),
      createAccountError: getShortText(
        'register.createAccountError.text',
        'Something went wrong while creating an account, please try again or contact us if the issue persists.'
      ),
    },
  };
};

interface RegistrationErrorMessage {
  invalidLoginDetails: string;
  createAccountError: string;
}

const parseRegisterError = (error: ApolloError, errorMessages: RegistrationErrorMessage): void => {
  const [err] = parseApolloErrors(error);

  const clientExpectedErrorCode = err?.clientExpectedErrorCode;
  switch (clientExpectedErrorCode) {
    case 'INVALID_LOGIN_DETAILS':
      message.warning(errorMessages.invalidLoginDetails, 5);
      break;
    case 'CREATE_ACCOUNT_ERROR':
      message.error(errorMessages.createAccountError);
      break;
    default:
      throw new TypeError(
        `Unexpected clientExpectedErrorCode(${clientExpectedErrorCode}) trying to create an account`
      );
  }
};

export interface RegisterContainerProps {
  readonly className?: string;
}

export function RegisterContainer({ className }: RegisterContainerProps): JSX.Element {
  const AppContext = useAppContext();
  const {
    cmsContent: { dictionary },
  } = AppContext;

  const segment = (Cookies.get('fixterSegment') || Segment.Fixter_UK) as Segment;

  const { formTitle, alreadyRegisteredText, loginLabel, errorMessages } = getCmsData(dictionary);

  const [loadingForm, setLoadingForm] = useState<boolean>(false);

  const onLoginClick = () => {
    changeUrl({
      pathname: '/login',
    });
  };

  const [createCustomerFromCreateAccount] = useCreateCustomer({
    onCompleted: () => {
      changeUrl({
        pathname: '/customer/account',
      });
    },
    onError: (err: ApolloError) => {
      setLoadingForm(false);
      try {
        return parseRegisterError(err, errorMessages);
      } catch (e) {
        return e;
      }
    },
  });

  const onSubmit = (values: MutationCreateCustomerParams) => {
    setLoadingForm(true);
    createCustomerFromCreateAccount({ ...values, segment });
  };

  return (
    <Styled className={classNames('RegisterContainer', className)}>
      <Title>{formTitle}</Title>
      <RegisterForm onSubmit={onSubmit} loading={loadingForm} />
      <span className="login">
        {alreadyRegisteredText}&nbsp;&nbsp;
        <SimpleLink
          type={2}
          text={loginLabel}
          href="/login"
          onClick={(event) => {
            event.preventDefault();
            onLoginClick();
          }}
        />
      </span>
    </Styled>
  );
}
